import React, { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import LightModeIcon from "@mui/icons-material/LightMode";
import List from "@mui/material/List";
import ListIcon from "@mui/icons-material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";

const drawerWidth = 240;
const navItems: Array<
  | { label: string; type: "route"; to: string }
  | { label: string; type: "section"; section: string }
> = [
  { label: "Home", type: "route", to: "/" },
  { label: "Features", type: "route", to: "/features/" },
  {
    label: "Smart Alerts",
    type: "route",
    to: "/smart-football-notifications/",
  },
  { label: "How It Works", type: "section", section: "how-it-works" },
  { label: "Pricing", type: "route", to: "/pricing/" },
  { label: "FAQ", type: "route", to: "/faq/" },
  { label: "Blog", type: "route", to: "/blog/" },
  { label: "Privacy & Terms", type: "section", section: "pages" },
];

function Navigation({ parentToChild, modeChange }: any) {
  const { mode } = parentToChild;
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAppBar, setShowAppBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  // Everything inside the menu closes it, and closing is idempotent: a tap on
  // an item also reaches the drawer's own click handler, and two toggles
  // cancelled out (the menu stayed open over the new page).
  const closeDrawer = () => setMobileOpen(false);

  // Any page change closes the menu too (links, the browser's back button).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setShowAppBar(false); // Scrolling down
      } else {
        setShowAppBar(true); // Scrolling up
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const scrollToSection = (section: string) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        scrollToTarget(section);
      }, 300);
    } else {
      scrollToTarget(section);
    }
  };

  const scrollToTarget = (section: string) => {
    if (section === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const targetElement = document.getElementById(section);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" });
      } else {
        console.error(`Element with id "${section}" not found`);
      }
    }
  };

  const drawer = (
    <Box
      className="navigation-bar-responsive"
      onClick={closeDrawer}
      sx={{
        textAlign: "center",
        height: "100%",
        backgroundColor: mode === "dark" ? "#111520" : "#ffffff",
      }}
    >
      <p className="mobile-menu-top">
        <ListIcon />
        Menu
      </p>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            {item.type === "route" ? (
              <ListItemButton
                component={RouterLink}
                to={item.to}
                sx={{ textAlign: "center" }}
                onClick={closeDrawer}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ) : (
              <ListItemButton
                sx={{ textAlign: "center" }}
                onClick={() => {
                  closeDrawer();
                  scrollToSection(item.section);
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        component="nav"
        id="navigation"
        className="navbar-fixed-top"
        sx={{
          transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease",
          transform: showAppBar ? "translateY(0)" : "translateY(-100%)",
          background: "rgba(10, 12, 18, 0.72)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderBottom: "1px solid rgba(255, 115, 0, 0.08)",
          boxShadow: "0 1px 40px rgba(0,0,0,0.35)",
        }}
      >
        <Toolbar className="navigation-bar">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon style={{ width: 24, height: 24 }} />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle theme"
            onClick={() => modeChange()}
            sx={{ mr: 1 }}
          >
            {mode === "dark" ? (
              <LightModeIcon style={{ width: 24, height: 24 }} />
            ) : (
              <DarkModeIcon style={{ width: 24, height: 24 }} />
            )}
          </IconButton>
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            {navItems.map((item) =>
              item.type === "route" ? (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.to}
                  className="nav-item"
                >
                  {item.label}
                </Button>
              ) : (
                <Button
                  key={item.label}
                  onClick={() => scrollToSection(item.section)}
                  className="nav-item"
                >
                  {item.label}
                </Button>
              ),
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={closeDrawer}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: mode === "dark" ? "#111520" : "#ffffff",
              color: mode === "dark" ? "#f0f0f0" : "#111111",
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}

export default Navigation;
